<template>
  <div :class="$style.wrap">
    <div :class="$style.card">
      <template v-if="!userLoggedIn">
        <h3>
          {{
            localizedStrings['article-vote-login-prompt'] ||
            'Please log in to vote if this article was helpful.'
          }}
        </h3>
        <BaseButton type="button" @click="handleLogin"> Login </BaseButton>
      </template>

      <template v-else>
        <h3>
          {{
            localizedStrings['article-vote-question'] ||
            'Was this article helpful?'
          }}
        </h3>

        <div :class="$style.controls">
          <button
            type="button"
            :disabled="!!articleVote"
            :aria-label="localizedStrings['helpful'] || 'Helpful'"
            :aria-pressed="articleVote === 'up'"
            :class="[
              $style.voteBtn,
              articleVote === 'up' && $style.voteBtnActive,
              articleVote && articleVote !== 'up' && $style.voteBtnDimmed,
            ]"
            @click="submitVote('up')"
          >
            <IconThumbsUp :class="$style.icon" />
          </button>

          <button
            type="button"
            :disabled="!!articleVote"
            :aria-label="localizedStrings['not-helpful'] || 'Not helpful'"
            :aria-pressed="articleVote === 'down'"
            :class="[
              $style.voteBtn,
              articleVote === 'down' && $style.voteBtnActive,
              articleVote && articleVote !== 'down' && $style.voteBtnDimmed,
            ]"
            @click="submitVote('down')"
          >
            <IconThumbsDown :class="$style.icon" />
          </button>
        </div>
      </template>
    </div>
  </div>
</template>

<script setup lang="ts">
const { handleLogin } = useAuthLogin()
const { localizedStrings } = useLocalizedStrings()

const props = defineProps<{
  articleId: string | undefined
}>()

const { loggedIn: userLoggedIn, accountId } = storeToRefs(useUserStore())

const VOTES_STORAGE_KEY_PREFIX = 'articleVotes'

const articleVote = ref<'up' | 'down' | null>(null)

const votesStorageKey = computed(
  () => `${VOTES_STORAGE_KEY_PREFIX}:${accountId.value || 'anonymous'}`,
)

const getArticleVotes = (): Record<string, 'up' | 'down'> => {
  try {
    const raw = localStorage.getItem(votesStorageKey.value)
    return raw ? JSON.parse(raw) : {}
  } catch {
    return {}
  }
}

const loadArticleVote = () => {
  if (!props.articleId) return
  const votes = getArticleVotes()
  articleVote.value = votes[props.articleId] ?? null
}

const submitVote = (direction: 'up' | 'down') => {
  if (!props.articleId || articleVote.value) return

  const votes = getArticleVotes()
  votes[props.articleId] = direction
  try {
    localStorage.setItem(votesStorageKey.value, JSON.stringify(votes))
  } catch {
    // localStorage may be unavailable
  }

  articleVote.value = direction

  pushToGaDataLayer({
    event: 'ga4.trackEvent',
    eventName: 'article_vote',
    eventParams: {
      action: direction,
      label: props.articleId,
    },
  })
}

// Reset and reload whenever the article changes (SPA navigation)
watch(
  [() => props.articleId, () => accountId.value, () => userLoggedIn.value],
  () => {
    articleVote.value = null
    loadArticleVote()
  },
)

onMounted(() => {
  loadArticleVote()
})
</script>

<style lang="scss" module>
.wrap {
  max-width: 1250px;
  margin: 0 auto;
  padding: 0 20px 40px;
}

.card {
  display: flex;
  align-items: center;
  flex-direction: column;
  gap: 16px;
  padding: 20px 24px;
  background: var(--white, #fff);
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  text-align: center;

  @include media('sm') {
    align-items: center;
    flex-direction: row;
    justify-content: space-between;
    text-align: left;
  }
}

.card h3 {
  margin: 0;
  text-wrap: balance;
}

.controls {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  flex-wrap: wrap;
  width: 100%;

  @include media('sm') {
    width: auto;
  }
}

.voteBtn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 38px;
  height: 38px;
  background: transparent;
  border: 1px solid #e5e7eb;
  border-radius: 6px;
  cursor: pointer;
  color: var(--purple);
  transition:
    background 0.15s,
    border-color 0.15s;

  &:disabled {
    cursor: default;
  }
}

.voteBtnActive {
  background: var(--purple, #4a4a8a);
  border-color: var(--purple, #4a4a8a);
  color: #fff;
}

.voteBtnDimmed {
  opacity: 0.35;
}

.icon {
  width: 18px;
  height: 18px;
  display: block;
}
</style>
